import { Eye, EyeOff, Layers, ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import type { DemoLayerStates, CropVariant } from "../types/agricultural-lulc";
import {
  LULC_COLORS,
  LULC_CHANGE_COLORS,
  CROP_RASTER_COLORS,
  CROP_HARVEST_COLORS,
  FOREST_COLORS,
} from "../config/agricultural-lulc";

interface ExploreLayerPanelProps {
  layers: DemoLayerStates;
  cropVariant: CropVariant;
  comparisonMode: boolean;
  onToggleLayer: (layer: keyof DemoLayerStates) => void;
  onOpacityChange: (layer: keyof DemoLayerStates, opacity: number) => void;
  onCropVariantChange: (variant: CropVariant) => void;
  onSentinelYearChange: (year: 2024 | 2025) => void;
  onSpeakToExpert: () => void;
}

interface LayerRowProps {
  label: string;
  visible: boolean;
  opacity: number;
  color?: string;
  disabled?: boolean;
  disabledReason?: string;
  onToggle: () => void;
  onOpacityChange: (value: number) => void;
}

function LayerRow({
  label,
  visible,
  opacity,
  color,
  disabled,
  disabledReason,
  onToggle,
  onOpacityChange,
}: LayerRowProps) {
  return (
    <div className="flex items-center gap-1.5 group/layer">
      {color && (
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-sm"
          style={{ backgroundColor: color }}
        />
      )}
      <span className="flex-1 text-xs font-medium">{label}</span>
      {visible && (
        <div className="flex items-center gap-1 opacity-0 group-hover/layer:opacity-100 transition-opacity">
          <span className="text-[10px] text-muted-foreground w-7 text-right">
            {Math.round(opacity * 100)}%
          </span>
          <Slider
            value={[opacity * 100]}
            min={0}
            max={100}
            step={5}
            className="w-16"
            onValueChange={([v]) => onOpacityChange(v / 100)}
          />
        </div>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        onClick={onToggle}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
      >
        {visible ? (
          <Eye className="h-3.5 w-3.5 text-foreground" />
        ) : (
          <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </Button>
    </div>
  );
}

interface LegendSectionProps {
  title: string;
  items: { label: string; color: string }[];
  defaultOpen?: boolean;
}

function LegendSection({ title, items, defaultOpen = true }: LegendSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors w-full"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {title}
      </button>
      {open && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1 pl-4">
          {items.map((item) => (
            <div key={item.label} className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-[10px] text-muted-foreground truncate">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ExploreLayerPanel({
  layers,
  cropVariant,
  comparisonMode,
  onToggleLayer,
  onOpacityChange,
  onCropVariantChange,
  onSentinelYearChange,
  onSpeakToExpert,
}: ExploreLayerPanelProps) {
  // Build legend sections from visible layers
  const legendSections: { title: string; items: { label: string; color: string }[] }[] = [];

  if (layers.lulc.visible) {
    if (layers.lulc.year === "change") {
      legendSections.push({
        title: "LULC Change",
        items: Object.entries(LULC_CHANGE_COLORS).map(([label, color]) => ({ label, color })),
      });
    } else {
      legendSections.push({
        title: `LULC ${layers.lulc.year}`,
        items: Object.entries(LULC_COLORS).map(([label, color]) => ({ label, color })),
      });
    }
  }

  if (layers.crops.visible) {
    const isHarvest = cropVariant === "harvest";
    legendSections.push({
      title: isHarvest ? "Harvest (summer 2024)" : "Crops (summer 2024)",
      items: Object.entries(isHarvest ? CROP_HARVEST_COLORS : CROP_RASTER_COLORS).map(
        ([label, color]) => ({ label, color })
      ),
    });
  }

  if (layers.forest.visible) {
    legendSections.push({
      title: "Forest",
      items: Object.entries(FOREST_COLORS).map(([label, color]) => ({ label, color })),
    });
  }

  return (
    <div className="w-[300px] max-h-[calc(100vh-6rem)] rounded-lg border border-border bg-card/90 backdrop-blur shadow-lg flex flex-col overflow-hidden">
      {/* Layers header */}
      <div className="flex items-center gap-1.5 px-3 pt-3 pb-2">
        <Layers className="h-3.5 w-3.5 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Layers</h3>
      </div>

      {/* Layer rows — ordered top-to-bottom by visual prominence: thematic
           rasters first (Forest entry layer at the top), then base layers. */}
      <div className="space-y-1 px-3 pb-2">
        <LayerRow
          label="Forest"
          visible={layers.forest.visible}
          opacity={layers.forest.opacity}
          color="#15803d"
          onToggle={() => onToggleLayer("forest")}
          onOpacityChange={(v) => onOpacityChange("forest", v)}
        />

        <LayerRow
          label="Crops & Harvest"
          visible={layers.crops.visible}
          opacity={layers.crops.opacity}
          color="#eab308"
          onToggle={() => onToggleLayer("crops")}
          onOpacityChange={(v) => onOpacityChange("crops", v)}
        />
        {layers.crops.visible && (
          <div className="flex items-center gap-1 pl-4 pr-1">
            <div className="flex items-center gap-0.5 rounded border border-border p-0.5">
              <Button
                variant={cropVariant === "crops" ? "default" : "ghost"}
                size="sm"
                className="h-5 px-2 text-[10px]"
                onClick={() => onCropVariantChange("crops")}
                title="Crop summer 2024"
              >
                Crops
              </Button>
              <Button
                variant={cropVariant === "harvest" ? "default" : "ghost"}
                size="sm"
                className="h-5 px-2 text-[10px]"
                onClick={() => onCropVariantChange("harvest")}
                title="Harvest summer 2024 (harvested vs standing per crop)"
              >
                Harvest
              </Button>
            </div>
          </div>
        )}

        <LayerRow
          label="LULC"
          visible={layers.lulc.visible}
          opacity={layers.lulc.opacity}
          color="#22c55e"
          disabled={comparisonMode}
          disabledReason="Comparison forces LULC on — disable comparison to hide it"
          onToggle={() => onToggleLayer("lulc")}
          onOpacityChange={(v) => onOpacityChange("lulc", v)}
        />

        <LayerRow
          label="Parcels"
          visible={layers.parcels.visible}
          opacity={layers.parcels.opacity}
          color="#a78bfa"
          onToggle={() => onToggleLayer("parcels")}
          onOpacityChange={(v) => onOpacityChange("parcels", v)}
        />

        <LayerRow
          label="LGA Boundary"
          visible={layers.lgaBoundary.visible}
          opacity={layers.lgaBoundary.opacity}
          color="#6366f1"
          onToggle={() => onToggleLayer("lgaBoundary")}
          onOpacityChange={(v) => onOpacityChange("lgaBoundary", v)}
        />

        <LayerRow
          label="Sentinel 2 Imagery"
          visible={layers.sentinel.visible}
          opacity={layers.sentinel.opacity}
          color="#0ea5e9"
          onToggle={() => onToggleLayer("sentinel")}
          onOpacityChange={(v) => onOpacityChange("sentinel", v)}
        />
        {layers.sentinel.visible && (
          <div className="flex items-center gap-1 pl-4 pr-1">
            <div className="flex items-center gap-0.5 rounded border border-border p-0.5">
              <Button
                variant={layers.sentinel.year === 2024 ? "default" : "ghost"}
                size="sm"
                className="h-5 px-2 text-[10px]"
                onClick={() => onSentinelYearChange(2024)}
              >
                2024
              </Button>
              <Button
                variant={layers.sentinel.year === 2025 ? "default" : "ghost"}
                size="sm"
                className="h-5 px-2 text-[10px]"
                onClick={() => onSentinelYearChange(2025)}
              >
                2025
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Speak to an Expert — sits below the layer list, above the legend. */}
      <Separator />
      <div className="px-3 py-2">
        <Button
          variant="default"
          size="sm"
          className="w-full h-7 text-xs"
          onClick={onSpeakToExpert}
        >
          Speak to an Expert
        </Button>
      </div>

      {/* Legend (shown when any thematic layer is visible) */}
      {legendSections.length > 0 && (
        <>
          <Separator />
          <div className="px-3 py-2 space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Legend</p>
            {legendSections.map((section) => (
              <LegendSection
                key={section.title}
                title={section.title}
                items={section.items}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
